import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_STYLES = {
  raw_funds_upload: "bg-emerald-50 text-emerald-700 border-emerald-300",
  raw_funds_row_edit: "bg-indigo-50 text-indigo-700 border-indigo-300",
  raw_funds_bulk_delete: "bg-rose-50 text-rose-700 border-rose-300",
  raw_funds_upload_delete: "bg-rose-50 text-rose-700 border-rose-300",
  raw_funds_assign_client_code: "bg-amber-50 text-amber-700 border-amber-300",
};

export default function RawFundsHistoryPage() {
  usePageTitle("Raw Data — Full History");
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/payment-verification/raw-funds/activity-history?limit=0`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await res.json();
        if (!res.ok) { toast.error("Failed to load history"); return; }
        setLogs(d.logs || []);
        setTotal(d.total || 0);
      } catch (e) {
        toast.error("Network error: " + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      JSON.stringify(l).toLowerCase().includes(q)
    );
  }, [logs, search]);

  const fmtDetails = (l) => {
    const d = l.details || {};
    if (l.action === "raw_funds_row_edit" && d.changes) {
      return Object.entries(d.changes)
        .map(([f, c]) => `${f}: "${c.from ?? "-"}" → "${c.to ?? "-"}"`)
        .join(" · ");
    }
    if (l.action === "raw_funds_upload") {
      return `${d.filename || ""} · ${d.row_count ?? "?"} rows${d.mode ? ` · mode=${d.mode}` : ""}${d.skipped_duplicates ? ` · ${d.skipped_duplicates} dups skipped` : ""}${d.replaced_rows ? ` · ${d.replaced_rows} replaced` : ""}`;
    }
    if (l.action === "raw_funds_upload_delete") {
      return `${d.filename || ""} · ${d.rows_deleted ?? "?"} rows deleted`;
    }
    if (l.action === "raw_funds_bulk_delete") {
      return `${d.deleted ?? "?"} of ${d.requested ?? "?"} rows deleted`;
    }
    if (l.action === "raw_funds_assign_client_code") {
      return `"${d.from || "(suspense)"}" → "${d.to || "(suspense)"}"${d.narration_preview ? ` · ${d.narration_preview}` : ""}`;
    }
    return JSON.stringify(d).slice(0, 160);
  };

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="raw-funds-history-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            Raw Data — Full Modification History
          </CardTitle>
          <CardDescription>
            Every upload, edit, deletion and client-code reassignment on the Raw Funds data — unlimited records ({total} total).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search action, user, filename, field, value..."
            className="max-w-md"
            data-testid="raw-funds-history-search"
          />
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading full history...
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs" data-testid="raw-funds-history-table">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="p-2 text-left">When (UTC)</th>
                    <th className="p-2 text-left">Action</th>
                    <th className="p-2 text-left">By</th>
                    <th className="p-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={4} className="p-6 text-center text-slate-400">No matching records</td></tr>
                  ) : filtered.map((l, i) => (
                    <tr key={l.id || i} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                      <td className="p-2 whitespace-nowrap font-mono">{String(l.timestamp || l.performed_at || "").replace("T", " ").slice(0, 19)}</td>
                      <td className="p-2 whitespace-nowrap">
                        <Badge variant="outline" className={`text-[10px] ${ACTION_STYLES[l.action] || "bg-slate-50 text-slate-600"}`}>
                          {l.action}
                        </Badge>
                      </td>
                      <td className="p-2 whitespace-nowrap font-medium">{l.performed_by_username || l.username || "-"}</td>
                      <td className="p-2 text-slate-700">{fmtDetails(l)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-[11px] text-slate-400">Showing {filtered.length} of {total} records</div>
        </CardContent>
      </Card>
    </div>
  );
}
